import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import confetti from 'canvas-confetti';
import { TEXTS } from '../constants';
import { GameBoat } from './svg/BoatSVG';

// Falling obstacle types
const OBSTACLES = ['🪨', '🌊', '⚓', '🦈', '🪸'];
const GAME_DURATION = 10; // seconds

const SwipeSteerGame = ({ onComplete }) => {
  const [gameState, setGameState] = useState('playing');
  const [boatX, setBoatX] = useState(50);
  const [timeLeft, setTimeLeft] = useState(GAME_DURATION);
  const [obstacles, setObstacles] = useState([]);
  const containerRef = useRef(null);
  const obstacleIdRef = useRef(0);
  const startTimeRef = useRef(Date.now());
  const animationFrameRef = useRef(null);
  const lastTouchXRef = useRef(null);

  // Wave boundaries narrow over time
  const safeZoneWidth = Math.max(30, 50 - (GAME_DURATION - timeLeft) * 2);
  const leftBoundary = 50 - safeZoneWidth / 2;
  const rightBoundary = 50 + safeZoneWidth / 2;
  const isCurrentlySafe = boatX >= leftBoundary && boatX <= rightBoundary;

  // Main game loop using requestAnimationFrame for smooth timer
  useEffect(() => {
    if (gameState !== 'playing') return;

    startTimeRef.current = Date.now();
    let lastObstacleSpawn = Date.now();

    const gameLoop = () => {
      const now = Date.now();
      const elapsed = (now - startTimeRef.current) / 1000;
      const remaining = Math.max(0, GAME_DURATION - elapsed);
      
      setTimeLeft(Math.ceil(remaining));

      // Spawn obstacles
      const spawnInterval = Math.max(600, 1200 - elapsed * 60);
      if (now - lastObstacleSpawn > spawnInterval) {
        lastObstacleSpawn = now;
        const newObstacle = {
          id: obstacleIdRef.current++,
          x: 15 + Math.random() * 70,
          y: -5,
          type: OBSTACLES[Math.floor(Math.random() * OBSTACLES.length)],
          speed: 2 + Math.random() * 2 + elapsed * 0.2
        };
        setObstacles(prev => [...prev, newObstacle]);
      }

      // Move obstacles
      setObstacles(prev => 
        prev
          .map(obs => ({ ...obs, y: obs.y + obs.speed * 0.5 }))
          .filter(obs => obs.y < 110)
      );

      // Check game end
      if (remaining <= 0) {
        setGameState('success');
        triggerConfetti();
        setTimeout(onComplete, 2500);
        return;
      }

      animationFrameRef.current = requestAnimationFrame(gameLoop);
    };

    animationFrameRef.current = requestAnimationFrame(gameLoop);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [gameState, onComplete]);

  // Touch handling with direct DOM manipulation for responsiveness
  useEffect(() => {
    const container = containerRef.current;
    if (!container || gameState !== 'playing') return;

    const handleTouchStart = (e) => {
      e.preventDefault();
      if (e.touches.length > 0) {
        lastTouchXRef.current = e.touches[0].clientX;
      }
    };

    const handleTouchMove = (e) => {
      e.preventDefault();
      if (e.touches.length > 0 && lastTouchXRef.current !== null) {
        const currentX = e.touches[0].clientX;
        const diff = currentX - lastTouchXRef.current;
        const containerWidth = container.offsetWidth;
        const movePercent = (diff / containerWidth) * 100 * 1.5;
        
        setBoatX(prev => Math.max(5, Math.min(95, prev + movePercent)));
        lastTouchXRef.current = currentX;
      }
    };

    const handleTouchEnd = (e) => {
      e.preventDefault();
      lastTouchXRef.current = null;
    };

    // Use passive: false to allow preventDefault
    container.addEventListener('touchstart', handleTouchStart, { passive: false });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd, { passive: false });
    container.addEventListener('touchcancel', handleTouchEnd, { passive: false });

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
      container.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, [gameState]);

  // Keyboard controls
  useEffect(() => {
    if (gameState !== 'playing') return;

    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        setBoatX(prev => Math.max(5, prev - 10));
      } else if (e.key === 'ArrowRight') {
        setBoatX(prev => Math.min(95, prev + 10));
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [gameState]);

  const triggerConfetti = () => {
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { x: 0.5, y: 0.6 },
      colors: ['#FACC15', '#0EA5E9', '#EC4899', '#22C55E']
    });
  };

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 flex flex-col items-center justify-center overflow-hidden no-select"
      style={{ touchAction: 'none' }}
      data-testid="steer-game-container"
    >
      <AnimatePresence mode="wait">
        {gameState === 'playing' && (
          <motion.div
            key="playing"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="w-full h-full relative"
          >
            {/* Title */}
            <div className="absolute top-6 left-0 right-0 text-center z-20 px-4">
              <motion.h2 
                className="text-xl font-black text-slate-900 mb-1"
                style={{ fontFamily: 'Nunito, sans-serif' }}
                data-testid="steer-title"
              >
                {TEXTS.steerGameTitle}
              </motion.h2>
              <p className="text-slate-600 text-xs">{TEXTS.steerGameSubtitle}</p>
              
              {/* Timer - large and prominent */}
              <motion.div 
                className="mt-3 text-5xl font-black"
                animate={{ 
                  color: timeLeft <= 3 ? '#EF4444' : '#0EA5E9',
                  scale: timeLeft <= 3 ? [1, 1.15, 1] : 1
                }}
                transition={{ duration: 0.3 }}
              >
                {timeLeft}
              </motion.div>
              <p className="text-sm text-slate-500">Sekunden</p>
            </div>

            {/* Game area */}
            <div className="absolute inset-0 top-36 bottom-20">
              {/* Left wave danger zone */}
              <motion.div
                className="absolute left-0 top-0 bottom-0 bg-gradient-to-r from-blue-500/70 to-blue-400/30"
                animate={{ width: `${leftBoundary}%` }}
                transition={{ duration: 0.5 }}
              >
                <div className="absolute right-0 top-0 bottom-0 w-6 bg-gradient-to-r from-transparent via-red-400/40 to-red-500/60" />
              </motion.div>

              {/* Right wave danger zone */}
              <motion.div
                className="absolute right-0 top-0 bottom-0 bg-gradient-to-l from-blue-500/70 to-blue-400/30"
                animate={{ width: `${100 - rightBoundary}%` }}
                transition={{ duration: 0.5 }}
              >
                <div className="absolute left-0 top-0 bottom-0 w-6 bg-gradient-to-l from-transparent via-red-400/40 to-red-500/60" />
              </motion.div>

              {/* Safe zone indicator */}
              <motion.div 
                className="absolute top-0 bottom-0 border-2 border-dashed border-green-400/60 rounded-lg"
                animate={{
                  left: `${leftBoundary}%`, 
                  width: `${safeZoneWidth}%`
                }}
                transition={{ duration: 0.5 }}
              />

              {/* Falling obstacles */}
              {obstacles.map(obstacle => (
                <motion.div
                  key={obstacle.id}
                  className="absolute text-3xl pointer-events-none"
                  style={{
                    left: `${obstacle.x}%`,
                    top: `${obstacle.y}%`,
                    transform: 'translate(-50%, -50%)'
                  }}
                  animate={{
                    rotate: [0, 360]
                  }}
                  transition={{
                    rotate: { duration: 2, repeat: Infinity, ease: 'linear' }
                  }}
                >
                  {obstacle.type}
                </motion.div>
              ))}

              {/* Boat */}
              <motion.div
                className="absolute pointer-events-none"
                style={{ 
                  left: `${boatX}%`,
                  top: '50%',
                  transform: 'translate(-50%, -50%)'
                }}
                animate={{
                  y: [0, -8, 0],
                  rotate: isCurrentlySafe ? [-2, 2, -2] : [-10, 10, -10]
                }}
                transition={{
                  y: { duration: 0.8, repeat: Infinity },
                  rotate: { duration: isCurrentlySafe ? 1.5 : 0.2, repeat: Infinity }
                }}
              >
                <GameBoat />
                {/* Status indicator */}
                <motion.div
                  className={`absolute -top-8 left-1/2 -translate-x-1/2 px-2 py-1 rounded-full text-xs font-bold whitespace-nowrap ${
                    isCurrentlySafe ? 'bg-green-400 text-green-900' : 'bg-red-400 text-red-900'
                  }`}
                  animate={{ scale: isCurrentlySafe ? 1 : [1, 1.2, 1] }}
                  transition={{ duration: 0.3 }}
                >
                  {isCurrentlySafe ? '✓ Guat!' : '⚠️ Welle!'}
                </motion.div>
              </motion.div>
            </div>

            {/* Swipe hint */}
            <div className="absolute bottom-8 left-0 right-0 text-center z-20">
              <motion.p
                animate={{ x: [-10, 10, -10] }}
                transition={{ duration: 1.5, repeat: Infinity }}
                className="text-slate-500 text-sm"
              >
                ← Wisch links/rechts →
              </motion.p>
            </div>
          </motion.div>
        )}

        {gameState === 'success' && (
          <motion.div
            key="success"
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: 'spring', stiffness: 200 }}
            className="text-center px-6"
          >
            <motion.div
              animate={{ rotate: [0, 10, -10, 0] }}
              transition={{ duration: 0.5, repeat: 3 }}
              className="text-7xl mb-6"
            >
              🎁
            </motion.div>
            <h2 
              className="text-2xl font-black text-slate-900 mb-2"
              style={{ fontFamily: 'Nunito, sans-serif' }}
              data-testid="steer-success-text"
            >
              2/2 erledigt!
            </h2>
            <h3 
              className="text-3xl font-black text-sky-600 mb-4"
              style={{ fontFamily: 'Nunito, sans-serif' }}
            >
              G'schenk freigschaltet!
            </h3>
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: '100%' }}
              transition={{ duration: 1.5 }}
              className="h-1 bg-gradient-to-r from-yellow-400 via-sky-400 to-pink-400 rounded-full max-w-xs mx-auto"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SwipeSteerGame;
